import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiRefreshCw, FiSearch, FiTrash2 } from 'react-icons/fi';
import DateRangePresetPicker from '../components/DateRangePresetPicker';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  listQuotations,
  deleteQuotation,
  sendQuotation,
  duplicateQuotation,
} from '../api';
import { hasPermission } from '../utils/permissions';
import {
  getQuotationStatusLabel,
  isDraftQuotationStatus,
  resolveQuotationListDeletedFilter,
} from '../utils/quotationStatus';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useQuotationSaleConversion from '../hooks/useQuotationSaleConversion';

const STATUS_STYLES = {
  draft: 'border-slate-200 bg-slate-100 text-slate-600',
  sent: 'border-blue-200 bg-blue-50 text-blue-700',
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  rejected: 'border-amber-200 bg-amber-50 text-amber-700',
  deleted: 'border-red-200 bg-red-50 text-red-700',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-CL');
};

const QuotationsListPage = () => {
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
    includeDeleted: false,
  });
  const [page, setPage] = useState(1);
  const [totalQuotations, setTotalQuotations] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const { user } = useCurrentUser();
  const canCreateQuotation = hasPermission(user, 'quotation.create');
  const canUpdateQuotation = hasPermission(user, 'quotation.update');
  const canDeleteQuotation = hasPermission(user, 'quotation.delete');
  const { confirm } = useConfirmDialog();
  const { requestConversion, conversionDialog } = useQuotationSaleConversion(branchId);
  const pageSize = 12;

  const fetchData = async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const data = await listQuotations(branchId, {
        status: filters.status || undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        isDeleted: resolveQuotationListDeletedFilter(filters),
        page,
        pageSize,
      });
      const normalized = normalizePaginatedResponse(data);
      setQuotations(normalized.data || []);
      setTotalQuotations(normalized.total || 0);
      setTotalPages(
        normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
      );
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [branchId, filters.status, filters.search, filters.startDate, filters.endDate, filters.includeDeleted, page]);

  const updateFilters = (changes) => {
    setFilters((current) => ({ ...current, ...changes }));
    setPage(1);
  };

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSend = async (id, hasEmail) => {
    if (!canUpdateQuotation) return;
    if (!hasEmail) {
      const approved = await confirm({
        title: 'Enviar cotización',
        message: 'El cliente no tiene correo. ¿Marcar como enviada de todas formas?',
        confirmLabel: 'Enviar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
      });
      if (!approved) return;
    } else {
      const approved = await confirm({
        title: 'Enviar cotización',
        message: '¿Enviar esta cotización por correo?',
        confirmLabel: 'Enviar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
      });
      if (!approved) return;
    }
    setActionLoadingId(id);
    try {
      await sendQuotation(branchId, id, { forceSend: !hasEmail });
      notify(hasEmail ? 'Correo enviado exitosamente' : 'Cotización marcada como enviada.');
      fetchData();
    } catch (err) {
      console.error(err);
      notify('No se pudo enviar la cotización, revise las credenciales o el correo del cliente');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteQuotation) return;
    const approved = await confirm({
      title: 'Eliminar cotización',
      message: '¿Eliminar esta cotización?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setActionLoadingId(id);
    try {
      await deleteQuotation(branchId, id);
      fetchData();
    } catch (err) {
      console.error(err);
      notify('No se pudo eliminar la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDuplicate = async (id) => {
    if (!canCreateQuotation) return;
    setActionLoadingId(id);
    try {
      const resp = await duplicateQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations/${resp.quotation.id}`);
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo duplicar la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConvert = async (id) => {
    if (!canUpdateQuotation) return;
    setActionLoadingId(id);
    try {
      const response = await requestConversion(id, quotations.find((entry) => entry.id === id)?.totalAmount);
      if (!response) return;
      const purchaseId = response?.purchase?.id;
      if (purchaseId) {
        notify('Cotización convertida. Redirigiendo a registrar pago.');
        navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt`);
        return;
      }
      notify('Cotización convertida, pero no se encontró la venta creada.');
      fetchData();
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo convertir la cotización');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <main className="record-list-page">
      {conversionDialog}
      <div className="mx-auto max-w-screen-2xl space-y-4">
        <section className="record-list-panel sticky top-14 z-30 md:top-0" aria-label="Gestión de cotizaciones">
          <div className="record-list-header">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="record-list-title">Cotizaciones</h1>
              <span className="border-l border-slate-200 pl-3 text-xs font-medium text-slate-500">{totalQuotations} cotizaciones</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={fetchData} disabled={loading}
                className="record-list-header-action disabled:cursor-not-allowed disabled:opacity-50">
                <FiRefreshCw aria-hidden="true" className={`h-4 w-4 ${loading ? 'animate-spin motion-reduce:animate-none' : ''}`} />
                Actualizar
              </button>
              {canCreateQuotation && (
                <button type="button" onClick={() => navigate(`/branches/${branchId}/quotations/new`)}
                  className="record-list-header-action">
                  <FiPlus aria-hidden="true" className="h-4 w-4" />Nueva cotización
                </button>
              )}
            </div>
          </div>
          <div className="space-y-3 p-3">
            <label className="relative block">
              <span className="sr-only">Buscar por cliente, RUT o ID</span>
              <FiSearch aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input type="search" className="record-list-input pl-9 pr-3"
                placeholder="Buscar por cliente, RUT o ID" value={filters.search}
                onChange={(e) => updateFilters({ search: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 items-start gap-3 lg:grid-cols-[1fr_1fr_auto]">
              <label className="block min-w-0">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Estado</span>
                <select className="record-list-input px-2" value={filters.status}
                  onChange={(e) => updateFilters({ status: e.target.value })}>
                  <option value="">Todos los estados</option>
                  <option value="draft">Borrador</option>
                  <option value="sent">Enviada</option>
                  <option value="accepted">Aceptada</option>
                  <option value="rejected">Rechazada</option>
                  <option value="deleted">Eliminada</option>
                </select>
              </label>
              <DateRangePresetPicker
                label="Rango de fechas" presetLabel="Rango de fechas"
                startLabel="Fecha inicio" endLabel="Fecha fin"
                startDate={filters.startDate} endDate={filters.endDate}
                onStartDateChange={(value) => updateFilters({ startDate: value })}
                onEndDateChange={(value) => updateFilters({ endDate: value })}
                containerClassName="min-w-0" labelClassName="sr-only"
                inputLabelClassName="mb-1 block text-xs font-semibold text-slate-500"
                selectClassName="record-list-input px-2" inputClassName="record-list-input px-2"
              />
              <label className="col-span-2 flex min-h-9 items-center gap-2 text-xs font-medium text-slate-600 lg:col-span-1 lg:mt-5">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-blue-600"
                  checked={filters.includeDeleted}
                  onChange={(e) => updateFilters({ includeDeleted: e.target.checked })} />
                Incluir eliminadas
              </label>
            </div>
          </div>
        </section>

        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}

        <div className="record-list-scroll" role="region" aria-label="Tabla de cotizaciones" tabIndex={0}>
          <table className="record-list-table min-w-[1050px]" aria-label="Lista de cotizaciones" aria-busy={loading}>
            <thead>
              <tr>
                <th scope="col">Cotización</th>
                <th scope="col">Cliente</th>
                <th scope="col">Actualizada</th>
                <th scope="col" className="text-right">Total</th>
                <th scope="col">Estado</th>
                <th scope="col" className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  <span role="status" aria-label="Cargando cotizaciones"><InlineLoading label="Cargando cotizaciones" /></span>
                </td></tr>
              ) : quotations.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  {error ? 'No se pudieron cargar las cotizaciones.' : 'No hay cotizaciones con los filtros actuales.'}
                </td></tr>
              ) : quotations.map((q) => {
                const hasEmail = !!q.Client?.email;
                const isDeleted = q.isDeleted === true || q.status === 'deleted';
                const status = isDeleted ? 'deleted' : String(q.status || '').trim().toLowerCase();
                const isDraft = isDraftQuotationStatus(q.status) && !isDeleted;
                const isWorking = actionLoadingId === q.id;
                return (
                  <tr key={q.id} className="record-list-row" data-cancelled={isDeleted} aria-busy={isWorking}>
                    <th scope="row" className="font-bold text-slate-950">
                      <button type="button" onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}`)}
                        aria-label={`Ver cotización #${q.id}`}
                        className="min-h-8 rounded px-1 text-left hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
                        #{q.id}
                      </button>
                    </th>
                    <td>
                      <div className="min-w-[180px] max-w-xs break-words font-medium text-slate-900">{q.Client?.name || 'Detalle sin cliente'}</div>
                      <div className="mt-1 text-xs text-slate-500">{q.Client?.rut || '—'}</div>
                      {!hasEmail && <span className="mt-1 block text-xs text-amber-700">Sin correo</span>}
                    </td>
                    <td className="text-xs text-slate-600">{formatDateTime(q.updatedAt)}</td>
                    <td className="whitespace-nowrap text-right font-bold tabular-nums text-slate-900">
                      ${formatPesoAmount(Number(q.totalAmount || 0))}
                    </td>
                    <td>
                      <span className={`record-list-badge whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
                        {getQuotationStatusLabel(status)}
                      </span>
                    </td>
                    <td>
                      <div className="ml-auto flex w-60 flex-wrap items-center justify-end gap-2 sm:w-80">
                        <button type="button"
                          onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}`)}
                          className="record-list-action"
                        >
                          Ver
                        </button>
                        {canUpdateQuotation && isDraft && (
                          <button type="button"
                            onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}/edit`)}
                            disabled={isWorking}
                            className="record-list-action record-list-action-primary"
                          >
                            {isWorking ? '...' : 'Editar'}
                          </button>
                        )}
                        {canCreateQuotation && (
                          <button type="button"
                            onClick={() => handleDuplicate(q.id)}
                            disabled={isWorking}
                            className="record-list-action"
                          >
                            {isWorking ? '...' : 'Duplicar'}
                          </button>
                        )}
                        {canUpdateQuotation && isDraft && (
                          <button type="button"
                            onClick={() => handleSend(q.id, hasEmail)}
                            disabled={isWorking}
                            className="record-list-action record-list-action-success"
                            title={!hasEmail ? 'El cliente no tiene correo' : undefined}
                          >
                            {isWorking ? '...' : 'Enviar'}
                          </button>
                        )}
                        {canUpdateQuotation && status === 'sent' && !isDeleted && (
                          <button type="button"
                            onClick={() => handleConvert(q.id)}
                            disabled={isWorking}
                            className="record-list-action record-list-action-success"
                          >
                            {isWorking ? '...' : 'Aceptar'}
                          </button>
                        )}
                        {canDeleteQuotation && isDraft && q.isDeleted !== true && (
                          <button type="button"
                            onClick={() => handleDelete(q.id)}
                            disabled={isWorking}
                            className="record-list-action record-list-action-danger"
                            title="Eliminar"
                            aria-label={`Eliminar cotización #${q.id}`}
                          >
                            {isWorking ? '...' : <FiTrash2 aria-hidden="true" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalQuotations > pageSize && (
          <div className="record-list-pagination">
            <div>
              Mostrando {totalQuotations === 0 ? 0 : (page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, totalQuotations)} de {totalQuotations}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={loading || page === 1}
                className="record-list-action"
              >
                Anterior
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={loading || page >= totalPages}
                className="record-list-action"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default QuotationsListPage;
